// src/pages/profile.js
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import {
  Bell,
  CreditCard,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LogOut,
  MapPin,
  MessageSquare,
  Package,
  Plus,
  Save,
  Settings,
  Tag,
  Trash2,
  User,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from '../components/AddressAutocomplete';
import BusinessUserPayment from '../components/BusinessUserPayment';
import PaymentMethodForm from '../components/PaymentMethodForm';
import SupportSection from '../components/SupportSection';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { config } = useConfig();

  //for card info
  const [showBusinessUpgradeForm, setShowBusinessUpgradeForm] = useState(false);
  const [businessPaymentIntentId, setBusinessPaymentIntentId] = useState(null);
  const [businessPaymentMethodId, setBusinessPaymentMethodId] = useState(null);
  const [businessPaymentProcessing, setBusinessPaymentProcessing] = useState(false);
  const [businessPaymentError, setBusinessPaymentError] = useState(null);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState(null); // Added for error handling
  const [paymentIntentId, setPaymentIntentId] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    currentPassword: '',
    newPassword: '',
    addresses: [],
    defaultAddress: '',
    userType: 'regular',
    preferences: {
      newsletter: true,
      marketingEmails: true,
      orderUpdates: true,
      promotionalAlerts: true,
    },
  });

  const [loginActivity, setLoginActivity] = useState([]);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Fetch the Stripe public key from the backend on mount
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        setIsStripeLoading(true);
        const { data } = await axios.get('/api/config/stripe');

        if (data.publicKey) {
          // Create the promise with the public key
          const promise = await loadStripe(data.publicKey);
          setStripePromise(promise);
        } else {
          throw new Error('No public key in response');
        }
      } catch (err) {
        console.error('Error fetching Stripe public key:', err);
        setStripeError('Could not load payment system. Please try again later.');
      } finally {
        setIsStripeLoading(false);
      }
    };

    fetchStripeKey();
  }, []);

  useEffect(() => {
    const fetchLoginActivity = async () => {
      if (activeTab === 'security') {
        try {
          setLoadingActivity(true);
          const response = await fetch('/api/users/login-activity', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          const data = await response.json();
          setLoginActivity(data);
        } catch (error) {
          console.error('Error fetching login activity:', error);
        } finally {
          setLoadingActivity(false);
        }
      }
    };

    fetchLoginActivity();
  }, [activeTab]);

  useEffect(() => {
    if (user) {
      setFormData(prevState => ({
        ...prevState,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        userType: user.userType || 'regular',
        addresses: user.addresses || [],
        defaultAddress: user.defaultAddress || '',
        preferences: {
          newsletter: user.preferences?.newsletter ?? true,
          marketingEmails: user.preferences?.marketingEmails ?? true,
          orderUpdates: user.preferences?.orderUpdates ?? true,
          promotionalAlerts: user.preferences?.promotionalAlerts ?? true,
        },
        paymentMethods: user.paymentMethods || [],
      }));
    }

    // Fetch orders if on orders tab
    if (activeTab === 'orders') {
      fetchOrders();
    }

    // Fetch coupons if on coupons tab
    if (activeTab === 'coupons') {
      fetchCoupons();
    }
  }, [user, activeTab]);

  const fetchOrders = async () => {
    try {
      const response = await fetch('/api/users/orders', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to fetch orders' });
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await fetch('/api/users/coupons', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      setCoupons(data);
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to fetch coupons' });
    }
  };

  const CopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = e => {
      e.preventDefault();
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <button
        onClick={handleCopy}
        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
      >
        {copied ? 'Copied!' : 'Copy Code'}
      </button>
    );
  };

  const handleAddAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        { street: '', city: '', state: '', postalCode: '', country: 'Canada' },
      ],
    }));
  };

  const handleRemoveAddress = index => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = { ...updatedAddresses[index], [field]: value };
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handleSetDefaultAddress = index => {
    setFormData(prev => ({
      ...prev,
      defaultAddress: prev.addresses[index]._id,
    }));
  };

  //function to handle account type change
  // const handleAccountTypeChange = async (newType) => {
  //   // If changing to regular, no payment needed
  //   if (newType === 'regular') {
  //     try {
  //       const response = await fetch('/api/users/update-account-type', {
  //         method: 'PUT',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'Authorization': `Bearer ${localStorage.getItem('token')}`
  //         },
  //         body: JSON.stringify({
  //           userType: 'regular'
  //         })
  //       });

  //       const data = await response.json();

  //       if (response.ok) {
  //         setFormData(prev => ({...prev, userType: 'regular'}));
  //         localStorage.setItem('user', JSON.stringify(data.user));
  //         localStorage.setItem('token', data.token);
  //         setMessage({ type: 'success', content: 'Account type updated to Regular' });
  //         setTimeout(() => window.location.reload(), 1000);
  //       } else {
  //         setMessage({ type: 'error', content: data.error || 'Failed to update account type' });
  //       }
  //     } catch (error) {
  //       setMessage({ type: 'error', content: 'An error occurred while updating account type' });
  //     }
  //   } else if (newType === 'business') {
  //     // Show business upgrade form for verification payment
  //     setShowBusinessUpgradeForm(true);
  //   }
  // };
  const handleAccountTypeChange = async newType => {
    // If no change, do nothing
    if (newType === formData.userType) {
      return;
    }

    // If changing to regular, no payment needed
    if (newType === 'regular') {
      try {
        setLoading(true);
        const response = await fetch('/api/users/update-account-type', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            userType: 'regular',
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Update local state first
          setFormData(prev => ({ ...prev, userType: 'regular' }));

          // Update localStorage
          const userJson = localStorage.getItem('user');
          if (userJson) {
            const userObj = JSON.parse(userJson);
            userObj.userType = 'regular';
            localStorage.setItem('user', JSON.stringify(userObj));
          }

          // Store new token if provided
          if (data.token) {
            localStorage.setItem('token', data.token);
          }

          setMessage({ type: 'success', content: 'Account type updated to Regular' });
        } else {
          setMessage({ type: 'error', content: data.error || 'Failed to update account type' });
        }
      } catch (error) {
        setMessage({ type: 'error', content: 'An error occurred while updating account type' });
      } finally {
        setLoading(false);
      }
    } else if (newType === 'business') {
      // Show business upgrade form for verification payment
      setShowBusinessUpgradeForm(true);
    }
  };

  // function to handle business verification payment completion
  const handleBusinessVerificationComplete = async (paymentIntentId, paymentMethodId) => {
    setBusinessPaymentIntentId(paymentIntentId);
    setBusinessPaymentMethodId(paymentMethodId);
    setBusinessPaymentProcessing(true);

    try {
      const response = await fetch('/api/users/update-account-type', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          userType: 'business',
          paymentIntentId,
          paymentMethodId: paymentMethodId || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Here's the fix - explicitly set userType in formData
        setFormData(prev => ({
          ...prev,
          userType: 'business',
        }));

        // Update localStorage with the business user type
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const userObj = JSON.parse(userJson);
          userObj.userType = 'business';
          localStorage.setItem('user', JSON.stringify(userObj));
        }

        // Store new token if provided
        if (data.token) {
          localStorage.setItem('token', data.token);
        }

        setMessage({ type: 'success', content: 'Account upgraded to Business successfully!' });
        setShowBusinessUpgradeForm(false);
      } else {
        setBusinessPaymentError(data.error || 'Failed to upgrade account');

        // Automatically refund if the account upgrade failed
        if (paymentIntentId) {
          try {
            await fetch('/api/refund-verification-payment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
              body: JSON.stringify({
                paymentIntentId,
                reason: 'Account upgrade failed',
              }),
            });
            console.log('Verification charge refunded due to failed account upgrade');
          } catch (refundError) {
            console.error('Failed to refund verification charge:', refundError);
          }
        }
      }
    } catch (error) {
      setBusinessPaymentError('An error occurred while upgrading account');
      // Attempt to refund
      if (paymentIntentId) {
        try {
          await fetch('/api/refund-verification-payment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              paymentIntentId: paymentIntentId,
              reason: 'Account upgrade error',
            }),
          });
        } catch (refundError) {
          console.error('Failed to refund verification charge:', refundError);
        }
      }
    } finally {
      setBusinessPaymentProcessing(false);
    }
  };
  // const handleBusinessVerificationComplete = async (paymentIntentId, paymentMethodId) => {
  //   setBusinessPaymentIntentId(paymentIntentId);
  //   setBusinessPaymentMethodId(paymentMethodId);
  //   setBusinessPaymentProcessing(true);

  //   try {
  //     const response = await fetch('/api/users/update-account-type', {
  //       method: 'PUT',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${localStorage.getItem('token')}`
  //       },
  //       body: JSON.stringify({
  //         userType: 'business',
  //         paymentIntentId,
  //         paymentMethodId: paymentMethodId || undefined
  //       })
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       // Update local state
  //       setFormData(prev => ({...prev, userType: 'business'}));

  //       // Update localStorage
  //       const userJson = localStorage.getItem('user');
  //       if (userJson) {
  //         const userObj = JSON.parse(userJson);
  //         userObj.userType = 'business';
  //         localStorage.setItem('user', JSON.stringify(userObj));
  //       }

  //       // Store new token if provided
  //       if (data.token) {
  //         localStorage.setItem('token', data.token);
  //       }

  //       setMessage({ type: 'success', content: 'Account upgraded to Business successfully!' });
  //       setShowBusinessUpgradeForm(false);
  //     } else {
  //       setBusinessPaymentError(data.error || 'Failed to upgrade account');
  //       // Attempt to refund the charge since upgrade failed
  //       try {
  //         await fetch('/api/refund-verification-payment', {
  //           method: 'POST',
  //           headers: {
  //             'Content-Type': 'application/json',
  //             'Authorization': `Bearer ${localStorage.getItem('token')}`
  //           },
  //           body: JSON.stringify({
  //             paymentIntentId,
  //             reason: 'Account upgrade failed'
  //           })
  //         });
  //       } catch (refundError) {
  //         console.error('Failed to refund verification charge:', refundError);
  //       }
  //     }
  //   } catch (error) {
  //     setBusinessPaymentError('An error occurred while upgrading account');
  //     // Attempt to refund - keep this part as is
  //   } finally {
  //     setBusinessPaymentProcessing(false);
  //   }
  // };

  const handleAddPaymentMethod = updatedPaymentMethods => {
    setFormData(prev => ({
      ...prev,
      paymentMethods: updatedPaymentMethods,
    }));
    setShowAddPaymentForm(false);
    setMessage({ type: 'success', content: 'Payment method added successfully' });

    // Update user object in localStorage
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      user.paymentMethods = updatedPaymentMethods;
      localStorage.setItem('user', JSON.stringify(user));
    }
  };

  const handleSetDefaultPaymentMethod = async paymentMethodId => {
    try {
      const response = await fetch('/api/users/payment-methods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          paymentMethodId,
          makeDefault: true,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          paymentMethods: data.paymentMethods,
        }));
        setMessage({ type: 'success', content: 'Default payment method updated' });

        // Update user object in localStorage
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          user.paymentMethods = data.paymentMethods;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to update payment method' });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'An error occurred while updating payment method' });
    }
  };

  const handleDeletePaymentMethod = async paymentMethodId => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      try {
        const response = await fetch(`/api/users/payment-methods/${paymentMethodId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        const data = await response.json();

        if (response.ok) {
          setFormData(prev => ({
            ...prev,
            paymentMethods: data.paymentMethods,
          }));
          setMessage({ type: 'success', content: 'Payment method removed' });

          // Update user object in localStorage
          const userJson = localStorage.getItem('user');
          if (userJson) {
            const user = JSON.parse(userJson);
            user.paymentMethods = data.paymentMethods;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } else {
          setMessage({ type: 'error', content: data.error || 'Failed to remove payment method' });
        }
      } catch (error) {
        setMessage({ type: 'error', content: 'An error occurred while removing payment method' });
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm('Are you sure you want to delete your account? This action cannot be undone.')
    ) {
      try {
        const response = await fetch('/api/users/profile', {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          logout();
          navigate('/');
        } else {
          setMessage({ type: 'error', content: 'Failed to delete account' });
        }
      } catch (error) {
        setMessage({ type: 'error', content: 'An error occurred while deleting account' });
      }
    }
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', content: '' });

    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', content: 'Profile updated successfully!' });
        localStorage.setItem('user', JSON.stringify(data.user));

        // Wait a short moment to show the success message
        setTimeout(() => {
          // Reload the page
          window.location.reload();
        }, 100);
      } else {
        setMessage({ type: 'error', content: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'An error occurred while updating profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const tabs = [
    { id: 'personal', label: 'Account Info', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'payments', label: 'Payment Methods', icon: CreditCard },
    { id: 'coupons', label: 'My Coupons', icon: Tag },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'support', label: 'Support', icon: MessageSquare },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'preferences', label: 'Preferences', icon: Settings },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company (Optional)</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Type</label>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center justify-between p-3 border rounded-md">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="regular-account"
                        name="userType"
                        checked={formData.userType === 'regular'}
                        onChange={e => {
                          e.preventDefault(); // Prevent form submission
                          if (formData.userType !== 'regular') {
                            if (
                              window.confirm(
                                'Are you sure you want to change your account to Regular?'
                              )
                            ) {
                              handleAccountTypeChange('regular');
                            }
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor="regular-account">
                        <span className="font-medium">Regular Customer</span>
                        <p className="text-sm text-gray-500">For personal use and shopping</p>
                      </label>
                    </div>
                    {formData.userType === 'regular' && (
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-md">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="business-account"
                        name="userType"
                        checked={formData.userType === 'business'}
                        onChange={e => {
                          e.preventDefault(); // Prevent form submission
                          if (formData.userType !== 'business') {
                            if (
                              window.confirm(
                                'Are you sure you want to upgrade to a Business account?'
                              )
                            ) {
                              handleAccountTypeChange('business');
                            }
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor="business-account">
                        <span className="font-medium">Business Customer</span>
                        <p className="text-sm text-gray-500">
                          For business purchases and bulk orders
                        </p>
                      </label>
                    </div>
                    {formData.userType === 'business' && (
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>
                </div>

                {/* Business upgrade payment form - keep this part unchanged */}
                {showBusinessUpgradeForm && (
                  <div className="mt-4 p-4 border rounded-md">
                    <h3 className="text-lg font-medium mb-3">Business Account Verification</h3>
                    <Elements stripe={stripePromise}>
                      <BusinessUserPayment
                        amount={config.businessUserChargingAmount || 1.0}
                        email={formData.email}
                        onPaymentComplete={handleBusinessVerificationComplete}
                        saveCardOption={true}
                        error={businessPaymentError}
                        processing={businessPaymentProcessing}
                      />
                    </Elements>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'address':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Manage Addresses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(formData.addresses || []).map((address, index) => (
                <div
                  key={index}
                  className="bg-white shadow-md rounded-lg p-6 border border-gray-200"
                >
                  <h4 className="text-lg font-semibold text-gray-700">Address {index + 1}</h4>
                  <div className="mt-4 space-y-2">
                    <AddressAutocomplete
                      index={index}
                      address={address}
                      onAddressChange={handleAddressChange}
                    />

                    <label className="block text-sm font-medium text-gray-600">City</label>
                    <input
                      type="text"
                      value={address.city}
                      onChange={e => handleAddressChange(index, 'city', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />

                    <label className="block text-sm font-medium text-gray-600">
                      State/Province
                    </label>
                    <input
                      type="text"
                      value={address.state}
                      onChange={e => handleAddressChange(index, 'state', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />

                    <label className="block text-sm font-medium text-gray-600">Postal Code</label>
                    <input
                      type="text"
                      value={address.postalCode}
                      onChange={e => handleAddressChange(index, 'postalCode', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />

                    <label className="block text-sm font-medium text-gray-600">Country</label>
                    <select
                      value={address.country}
                      onChange={e => handleAddressChange(index, 'country', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="Canada">Canada</option>
                      <option value="United States">United States</option>
                    </select>
                  </div>

                  <div className="flex justify-between mt-4">
                    <button
                      type="button"
                      onClick={() => handleRemoveAddress(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetDefaultAddress(index)}
                      className={`text-blue-600 hover:underline ${
                        formData.defaultAddress === address._id ? 'font-bold' : ''
                      }`}
                    >
                      {formData.defaultAddress === address._id ? 'Default' : 'Set as Default'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleAddAddress}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add New Address
            </button>
          </div>
        );

      case 'orders':
        // Ensure orders is an array
        const orderArray = Array.isArray(orders)
          ? orders
          : orders && typeof orders === 'object' && Object.values(orders).length > 0
          ? Object.values(orders)
          : [];

        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Order History</h2>
            {orderArray.length > 0 ? (
              <div className="grid gap-4">
                {orderArray.map(order => {
                  // Ensure order is a valid object
                  if (!order || typeof order !== 'object') return null;

                  const orderId = order._id || 'unknown';
                  const createdAt = order.createdAt ? new Date(order.createdAt) : new Date();
                  const totalAmount = order.totalAmount || 0;
                  const status = order.status || 'pending';

                  return (
                    <div
                      key={orderId}
                      className="bg-white rounded-lg shadow p-4 border border-gray-200"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-800 truncate">
                          Order #
                          {typeof orderId === 'string'
                            ? orderId.substring(Math.max(0, orderId.length - 8))
                            : orderId}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full 
                            ${
                              status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : status === 'processing'
                                ? 'bg-yellow-100 text-yellow-800'
                                : status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Date</p>
                          <p>{createdAt.toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total</p>
                          <p className="font-medium">${parseFloat(totalAmount).toFixed(2)}</p>
                        </div>
                      </div>

                      <div className="mt-4 pt-2 border-t border-gray-100">
                        <a
                          href={`/orders`}
                          className="block w-full text-center py-2 text-blue-600 font-medium rounded hover:bg-blue-50"
                        >
                          View Details
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="flex justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 160" className="w-40">
                    {/* Background with subtle gradient */}
                    <defs>
                      <linearGradient id="emptyOrderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#fcfcfd" stopOpacity="1" />
                        <stop offset="100%" stopColor="#f9fafb" stopOpacity="1" />
                      </linearGradient>
                      <filter id="orderShadow" x="-10%" y="-10%" width="120%" height="120%">
                        <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.05" />
                      </filter>
                    </defs>

                    {/* Order paper */}
                    <rect
                      x="70"
                      y="20"
                      width="100"
                      height="120"
                      rx="4"
                      fill="white"
                      stroke="#e5e7eb"
                      strokeWidth="1.5"
                      filter="url(#orderShadow)"
                    />

                    {/* Order lines */}
                    <line x1="85" y1="40" x2="155" y2="40" stroke="#e5e7eb" strokeWidth="1.5" />
                    <line x1="85" y1="60" x2="155" y2="60" stroke="#e5e7eb" strokeWidth="1.5" />
                    <line x1="85" y1="80" x2="155" y2="80" stroke="#e5e7eb" strokeWidth="1.5" />
                    <line x1="85" y1="100" x2="155" y2="100" stroke="#e5e7eb" strokeWidth="1.5" />

                    {/* Search icon */}
                    <circle
                      cx="150"
                      cy="70"
                      r="30"
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth="1.5"
                      opacity="0.7"
                    />
                    <line
                      x1="170"
                      y1="90"
                      x2="185"
                      y2="105"
                      stroke="#9ca3af"
                      strokeWidth="2"
                      opacity="0.7"
                    />

                    {/* X mark for "not found" */}
                    <line x1="120" y1="50" x2="140" y2="70" stroke="#d1d5db" strokeWidth="2" />
                    <line x1="140" y1="50" x2="120" y2="70" stroke="#d1d5db" strokeWidth="2" />
                  </svg>
                </div>
                <p className="text-gray-500">No orders found.</p>
              </div>
            )}
          </div>
        );

      case 'coupons':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">My Coupons</h2>
            {coupons.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {coupons.map(coupon => (
                  <div
                    key={coupon._id}
                    className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Tag className="h-5 w-5 text-blue-600" />
                          <h3 className="text-lg font-medium text-blue-600">{coupon.code}</h3>
                        </div>
                        <p className="text-2xl font-bold">
                          {coupon.discountType === 'percentage'
                            ? `${coupon.discountValue}% OFF`
                            : `$${coupon.discountValue} OFF`}
                        </p>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">
                            Minimum purchase: ${coupon.minimumPurchase || 0}
                          </p>
                          {coupon.maxUsesPerUser > 0 && (
                            <p className="text-sm text-gray-600">
                              Max Uses:{' '}
                              {coupon.maxUsesPerUser - (coupon.userUsage?.usageCount || 0)}
                            </p>
                          )}
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          new Date(coupon.endDate) > new Date()
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {new Date(coupon.endDate) > new Date() ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                          Valid until: {new Date(coupon.endDate).toLocaleDateString()}
                        </div>
                        {/* <button 
                            onClick={() => navigator.clipboard.writeText(coupon.code)}
                            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            Copy Code
                          </button> */}
                        <CopyButton code={coupon.code} />
                      </div>
                      {coupon.conditions && (
                        <p className="mt-2 text-sm text-gray-500">
                          Conditions: {coupon.conditions}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Coupons Available</h3>
                <p className="mt-2 text-gray-500">Check back later for new offers and discounts.</p>
              </div>
            )}
          </div>
        );

      case 'support':
        return (
          <div>
            <SupportSection />
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Payment Methods</h2>
              <button
                onClick={() => setShowAddPaymentForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Card
              </button>
            </div>

            {formData.paymentMethods && formData.paymentMethods.length > 0 ? (
              <div className="space-y-4">
                {formData.paymentMethods.map((method, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-4 bg-white flex justify-between items-center"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 flex-shrink-0">
                        {method.brand === 'visa' && (
                          <span className="text-blue-600 text-xl">Visa</span>
                        )}
                        {method.brand === 'mastercard' && (
                          <span className="text-red-600 text-xl">MC</span>
                        )}
                        {method.brand === 'amex' && (
                          <span className="text-blue-700 text-xl">Amex</span>
                        )}
                        {!['visa', 'mastercard', 'amex'].includes(method.brand) && (
                          <CreditCard className="h-6 w-6 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">•••• •••• •••• {method.last4}</p>
                        <p className="text-sm text-gray-500">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </p>
                        {method.isDefault && (
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {!method.isDefault && (
                        <button
                          onClick={() => handleSetDefaultPaymentMethod(method.paymentMethodId)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePaymentMethod(method.paymentMethodId)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Payment Methods Added</h3>
                <p className="mt-2 text-gray-500">Add a payment method to speed up checkout.</p>
              </div>
            )}

            {showAddPaymentForm && (
              <div className="mt-4 p-4 border rounded-md">
                <h3 className="text-lg font-medium mb-3">Add Payment Method</h3>
                <Elements stripe={stripePromise}>
                  <PaymentMethodForm
                    onComplete={handleAddPaymentMethod}
                    onCancel={() => setShowAddPaymentForm(false)}
                    error={paymentMethodError}
                  />
                </Elements>
              </div>
            )}
          </div>
        );
      // return (
      //   <div className="space-y-4">
      //     <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
      //     <div className="space-y-4">
      //       {/* Sample saved payment methods */}
      //       <div className="border rounded-lg p-4 bg-white">
      //         <div className="flex justify-between items-center">
      //           <div className="flex items-center space-x-4">
      //             <CreditCard className="h-6 w-6 text-gray-500" />
      //             <div>
      //               <p className="font-medium">•••• •••• •••• 4242</p>
      //               <p className="text-sm text-gray-500">Expires 12/25</p>
      //             </div>
      //           </div>
      //           <button className="text-red-600 hover:text-red-800">
      //             <Trash2 className="h-5 w-5" />
      //           </button>
      //         </div>
      //       </div>

      //       <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
      //         <Plus className="h-5 w-5" />
      //         <span>Add New Payment Method</span>
      //       </button>
      //     </div>
      //   </div>
      // );

      case 'security':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
            <div className="space-y-6">
              {/* Password Change Section */}
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Change Password</h3>
                <div className="space-y-4">
                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">Current Password</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        style={{
                          boxShadow: 'none',
                          border: 'none',
                          background: 'transparent',
                          transform: 'none',
                          padding: 0,
                          overflow: 'visible',
                          marginTop: -10 + 'px',
                        }}
                      >
                        {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        style={{
                          boxShadow: 'none',
                          border: 'none',
                          background: 'transparent',
                          transform: 'none',
                          padding: 0,
                          overflow: 'visible',
                          marginTop: -10 + 'px',
                        }}
                      >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Login Activity Section */}
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Login Activity</h3>
                <div className="space-y-4">
                  {loadingActivity ? (
                    <div className="flex justify-center">
                      <Loader2 className="animate-spin" size={24} />
                    </div>
                  ) : (
                    <>
                      {loginActivity
                        .slice(0, showAllActivity ? undefined : 1)
                        .map((activity, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center border-b pb-2"
                          >
                            <div>
                              <p className="font-medium">
                                Login from {activity.location || 'Unknown Location'}
                              </p>
                              <p className="text-sm text-gray-500">
                                {activity.device ? activity.device.split('(')[0] : 'Unknown Device'}
                              </p>
                            </div>
                            <p className="text-sm text-gray-500">
                              {new Date(activity.timestamp).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          setShowAllActivity(!showAllActivity);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {showAllActivity ? 'Show Less' : 'View all activity'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Account Deletion Section */}
              {/* <div className="bg-white p-6 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Account Deletion</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleDeleteAccount()}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    Delete Account
                  </button>
                </div> */}
            </div>
          </div>
        );

      case 'notifications':
        const handlePreferenceChange = async e => {
          e.preventDefault();
          const { name, checked } = e.target;
          const [section, field] = name.split('.');

          // Create a new preferences object with all existing values
          const updatedPreferences = {
            ...formData.preferences,
            [field]: checked,
          };

          // Update local state immediately for better UX
          setFormData(prev => ({
            ...prev,
            preferences: updatedPreferences,
          }));

          try {
            const response = await fetch('/api/users/preferences', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
              body: JSON.stringify({
                preferences: updatedPreferences,
              }),
            });

            const data = await response.json();

            if (response.ok) {
              // Update with the complete preferences object from the server
              setFormData(prev => ({
                ...prev,
                preferences: data.preferences,
              }));
              setMessage({ type: 'success', content: 'Preferences updated successfully!' });
            } else {
              // Revert local state if update failed
              setFormData(prev => ({
                ...prev,
                preferences: {
                  ...prev.preferences,
                  [field]: !checked,
                },
              }));
              setMessage({ type: 'error', content: data.error || 'Failed to update preferences' });
            }
          } catch (error) {
            // Revert local state if update failed
            setFormData(prev => ({
              ...prev,
              preferences: {
                ...prev.preferences,
                [field]: !checked,
              },
            }));
            console.error('Error updating preferences:', error);
            setMessage({ type: 'error', content: 'Error updating preferences' });
          }
        };

        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Order Updates</h3>
                  <p className="text-sm text-gray-500">
                    Receive notifications about your order status
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.orderUpdates"
                    checked={formData.preferences.orderUpdates}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Promotional Emails</h3>
                  <p className="text-sm text-gray-500">
                    Receive emails about sales and special offers
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.marketingEmails"
                    checked={formData.preferences.marketingEmails}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Newsletter</h3>
                  <p className="text-sm text-gray-500">Receive our monthly newsletter</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.newsletter"
                    checked={formData.preferences.newsletter}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                <div>
                  <h3 className="font-medium">Marketing Checkout</h3>
                  <p className="text-sm text-gray-500">
                    Receive marketing communications during checkout
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="preferences.promotionalAlerts"
                    checked={formData.preferences.promotionalAlerts}
                    onChange={handlePreferenceChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Account Preferences</h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Language & Region</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Language</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      defaultValue="en"
                    >
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time Zone</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      defaultValue="EST"
                    >
                      <option value="EST">Eastern Time (ET)</option>
                      <option value="CST">Central Time (CT)</option>
                      <option value="MST">Mountain Time (MT)</option>
                      <option value="PST">Pacific Time (PT)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Privacy Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Profile Visibility</p>
                      <p className="text-sm text-gray-500">Control who can see your profile</p>
                    </div>
                    <select
                      className="p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      defaultValue="private"
                    >
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="py-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (window.innerWidth <= 768) {
                      window.scrollBy({ top: 300, behavior: 'smooth' });
                    }
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-200 font-normal text-blue-900'
                      : 'text-gray-600 font-normal hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}

            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Log Out</span>
            </button>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              {message.content && (
                <Alert
                  variant={message.type === 'error' ? 'destructive' : 'default'}
                  className="mb-6"
                >
                  <AlertDescription>{message.content}</AlertDescription>
                </Alert>
              )}

              {/* Only wrap form around editable sections */}
              {['personal', 'address', 'notifications', 'security', 'preferences'].includes(
                activeTab
              ) ? (
                <form onSubmit={handleSubmit}>
                  {renderTabContent()}
                  <div className="mt-6 flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={20} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2" size={20} />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                renderTabContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

// // working properly
// // src/pages/profile.js
// import React, { useState, useEffect } from 'react';
// import { useAuth } from '../context/AuthContext';
// import SupportSection from '../components/SupportSection';
// import { useNavigate } from 'react-router-dom';
// import { Alert, AlertDescription } from '../components/ui/alert';
// import AddressAutocomplete from '../components/AddressAutocomplete';
// import { User, MapPin, CreditCard, Bell, Package, Tag, Lock, Settings, LogOut,
//    Loader2, Save, Eye, EyeOff, Plus, Trash2, MessageSquare, } from 'lucide-react';
// import { Elements } from '@stripe/react-stripe-js';
// import { loadStripe } from '@stripe/stripe-js';
// import BusinessUserPayment from '../components/BusinessUserPayment';
// import PaymentMethodForm from '../components/PaymentMethodForm';
// import { useConfig } from '../context/ConfigContext';
// import axios from 'axios';

// const Profile = () => {
//   const { user, logout } = useAuth();
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState('personal');
//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState({ type: '', content: '' });
//   const [orders, setOrders] = useState([]);
//   const [coupons, setCoupons] = useState([]);
//   const [showCurrentPassword, setShowCurrentPassword] = useState(false);
//   const [showNewPassword, setShowNewPassword] = useState(false);
//   const { config } = useConfig();

//   //for card info
//   const [showBusinessUpgradeForm, setShowBusinessUpgradeForm] = useState(false);
//   const [businessPaymentIntentId, setBusinessPaymentIntentId] = useState(null);
//   const [businessPaymentMethodId, setBusinessPaymentMethodId] = useState(null);
//   const [businessPaymentProcessing, setBusinessPaymentProcessing] = useState(false);
//   const [businessPaymentError, setBusinessPaymentError] = useState(null);
//   const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
//   const [paymentMethodError, setPaymentMethodError] = useState(null);
//   const [stripePromise, setStripePromise] = useState(null);
//   const [isStripeLoading, setIsStripeLoading] = useState(true);
//   const [stripeError, setStripeError] = useState(null); // Added for error handling
//   const [paymentIntentId, setPaymentIntentId] = useState(false);
//   const [paymentMethodId, setPaymentMethodId] = useState(false);

//   const [formData, setFormData] = useState({
//     firstName: '',
//     lastName: '',
//     email: '',
//     phone: '',
//     company: '',
//     currentPassword: '',
//     newPassword: '',
//     addresses: [],
//     defaultAddress: '',
//     userType: 'regular',
//     preferences: {
//       newsletter: false,
//       marketingEmails: false,
//       orderUpdates: true,
//       promotionalAlerts: false
//     }
//   });

//   const [loginActivity, setLoginActivity] = useState([]);
//   const [showAllActivity, setShowAllActivity] = useState(false);
//   const [loadingActivity, setLoadingActivity] = useState(false);

//   // Fetch the Stripe public key from the backend on mount
//   useEffect(() => {
//     const fetchStripeKey = async () => {
//       try {
//         setIsStripeLoading(true);
//         const { data } = await axios.get('/api/config/stripe');
//         console.log('Stripe key response:', data);

//         if (data.publicKey) {
//           // Create the promise with the public key
//           const promise = await loadStripe(data.publicKey);
//           setStripePromise(promise);
//         } else {
//           throw new Error('No public key in response');
//         }
//       } catch (err) {
//         console.error("Error fetching Stripe public key:", err);
//         setStripeError("Could not load payment system. Please try again later.");
//       } finally {
//         setIsStripeLoading(false);
//       }
//     };

//     fetchStripeKey();
//   }, []);

//   useEffect(() => {
//     const fetchLoginActivity = async () => {
//       if (activeTab === 'security') {
//         try {
//           setLoadingActivity(true);
//           const response = await fetch('/api/users/login-activity', {
//             headers: {
//               'Authorization': `Bearer ${localStorage.getItem('token')}`
//             }
//           });
//           const data = await response.json();
//           setLoginActivity(data);
//         } catch (error) {
//           console.error('Error fetching login activity:', error);
//         } finally {
//           setLoadingActivity(false);
//         }
//       }
//     };

//     fetchLoginActivity();
//   }, [activeTab]);

//   useEffect(() => {
//     if (user) {
//       setFormData(prevState => ({
//         ...prevState,
//         firstName: user.firstName || '',
//         lastName: user.lastName || '',
//         email: user.email || '',
//         phone: user.phone || '',
//         company: user.company || '',
//         userType: user.userType || 'regular',
//         addresses: user.addresses || [],
//         defaultAddress: user.defaultAddress || '',
//         preferences: user.preferences || prevState.preferences,
//         paymentMethods: user.paymentMethods || [],
//       }));
//     }

//     // Fetch orders if on orders tab
//     if (activeTab === 'orders') {
//       fetchOrders();
//     }

//     // Fetch coupons if on coupons tab
//     if (activeTab === 'coupons') {
//       fetchCoupons();
//     }
//   }, [user, activeTab]);

//   const fetchOrders = async () => {
//     try {
//       const response = await fetch('/api/users/orders', {
//         headers: {
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         }
//       });
//       const data = await response.json();
//       setOrders(data);
//     } catch (error) {
//       setMessage({ type: 'error', content: 'Failed to fetch orders' });
//     }
//   };

//   const fetchCoupons = async () => {
//     try {
//       const response = await fetch('/api/users/coupons', {
//         headers: {
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         }
//       });
//       const data = await response.json();
//       setCoupons(data);
//     } catch (error) {
//       setMessage({ type: 'error', content: 'Failed to fetch coupons' });
//     }
//   };

//   const CopyButton = ({ code }) => {
//     const [copied, setCopied] = useState(false);

//     const handleCopy = (e) => {
//       e.preventDefault();
//       navigator.clipboard.writeText(code);
//       setCopied(true);
//       setTimeout(() => setCopied(false), 2000);
//     };

//     return (
//       <button
//         onClick={handleCopy}
//         className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
//       >
//         {copied ? 'Copied!' : 'Copy Code'}
//       </button>
//     );
//   };

//   const handleAddAddress = () => {
//     setFormData(prev => ({
//       ...prev,
//       addresses: [...prev.addresses, { street: '', city: '', state: '', postalCode: '', country: 'Canada' }]
//     }));
//   };

//   const handleRemoveAddress = (index) => {
//     setFormData(prev => ({
//       ...prev,
//       addresses: prev.addresses.filter((_, i) => i !== index)
//     }));
//   };

//   const handleAddressChange = (index, field, value) => {
//     setFormData(prev => {
//       const updatedAddresses = [...prev.addresses];
//       updatedAddresses[index] = { ...updatedAddresses[index], [field]: value };
//       return { ...prev, addresses: updatedAddresses };
//     });
//   };

//   const handleSetDefaultAddress = (index) => {
//     setFormData(prev => ({
//       ...prev,
//       defaultAddress: prev.addresses[index]._id
//     }));
//   };

//   //function to handle account type change
//   // const handleAccountTypeChange = async (newType) => {
//   //   // If changing to regular, no payment needed
//   //   if (newType === 'regular') {
//   //     try {
//   //       const response = await fetch('/api/users/update-account-type', {
//   //         method: 'PUT',
//   //         headers: {
//   //           'Content-Type': 'application/json',
//   //           'Authorization': `Bearer ${localStorage.getItem('token')}`
//   //         },
//   //         body: JSON.stringify({
//   //           userType: 'regular'
//   //         })
//   //       });

//   //       const data = await response.json();

//   //       if (response.ok) {
//   //         setFormData(prev => ({...prev, userType: 'regular'}));
//   //         localStorage.setItem('user', JSON.stringify(data.user));
//   //         localStorage.setItem('token', data.token);
//   //         setMessage({ type: 'success', content: 'Account type updated to Regular' });
//   //         setTimeout(() => window.location.reload(), 1000);
//   //       } else {
//   //         setMessage({ type: 'error', content: data.error || 'Failed to update account type' });
//   //       }
//   //     } catch (error) {
//   //       setMessage({ type: 'error', content: 'An error occurred while updating account type' });
//   //     }
//   //   } else if (newType === 'business') {
//   //     // Show business upgrade form for verification payment
//   //     setShowBusinessUpgradeForm(true);
//   //   }
//   // };
//   const handleAccountTypeChange = async (newType) => {
//     // If no change, do nothing
//     if (newType === formData.userType) {
//       return;
//     }

//     // If changing to regular, no payment needed
//     if (newType === 'regular') {
//       try {
//         setLoading(true);
//         const response = await fetch('/api/users/update-account-type', {
//           method: 'PUT',
//           headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${localStorage.getItem('token')}`
//           },
//           body: JSON.stringify({
//             userType: 'regular'
//           })
//         });

//         const data = await response.json();

//         if (response.ok) {
//           // Update local state first
//           setFormData(prev => ({...prev, userType: 'regular'}));

//           // Update localStorage
//           const userJson = localStorage.getItem('user');
//           if (userJson) {
//             const userObj = JSON.parse(userJson);
//             userObj.userType = 'regular';
//             localStorage.setItem('user', JSON.stringify(userObj));
//           }

//           // Store new token if provided
//           if (data.token) {
//             localStorage.setItem('token', data.token);
//           }

//           setMessage({ type: 'success', content: 'Account type updated to Regular' });
//         } else {
//           setMessage({ type: 'error', content: data.error || 'Failed to update account type' });
//         }
//       } catch (error) {
//         setMessage({ type: 'error', content: 'An error occurred while updating account type' });
//       } finally {
//         setLoading(false);
//       }
//     } else if (newType === 'business') {
//       // Show business upgrade form for verification payment
//       setShowBusinessUpgradeForm(true);
//     }
//   };

//   // function to handle business verification payment completion
//   const handleBusinessVerificationComplete = async (paymentIntentId, paymentMethodId) => {
//     setBusinessPaymentIntentId(paymentIntentId);
//     setBusinessPaymentMethodId(paymentMethodId);
//     setBusinessPaymentProcessing(true);

//     try {
//       const response = await fetch('/api/users/update-account-type', {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         },
//         body: JSON.stringify({
//           userType: 'business',
//           paymentIntentId,
//           paymentMethodId: paymentMethodId || undefined
//         })
//       });

//       const data = await response.json();

//       if (response.ok) {
//         // Here's the fix - explicitly set userType in formData
//         setFormData(prev => ({
//           ...prev,
//           userType: 'business'
//         }));

//         // Update localStorage with the business user type
//         const userJson = localStorage.getItem('user');
//         if (userJson) {
//           const userObj = JSON.parse(userJson);
//           userObj.userType = 'business';
//           localStorage.setItem('user', JSON.stringify(userObj));
//         }

//         // Store new token if provided
//         if (data.token) {
//           localStorage.setItem('token', data.token);
//         }

//         setMessage({ type: 'success', content: 'Account upgraded to Business successfully!' });
//         setShowBusinessUpgradeForm(false);
//       } else {
//         setBusinessPaymentError(data.error || 'Failed to upgrade account');

//         // Automatically refund if the account upgrade failed
//         if (paymentIntentId) {
//           try {
//             await fetch('/api/refund-verification-payment', {
//               method: 'POST',
//               headers: {
//                 'Content-Type': 'application/json',
//                 'Authorization': `Bearer ${localStorage.getItem('token')}`
//               },
//               body: JSON.stringify({
//                 paymentIntentId,
//                 reason: 'Account upgrade failed'
//               })
//             });
//             console.log('Verification charge refunded due to failed account upgrade');
//           } catch (refundError) {
//             console.error('Failed to refund verification charge:', refundError);
//           }
//         }
//       }
//     } catch (error) {
//       setBusinessPaymentError('An error occurred while upgrading account');
//       // Attempt to refund
//       if (paymentIntentId) {
//         try {
//           await fetch('/api/refund-verification-payment', {
//             method: 'POST',
//             headers: {
//               'Content-Type': 'application/json',
//               'Authorization': `Bearer ${localStorage.getItem('token')}`
//             },
//             body: JSON.stringify({
//               paymentIntentId: paymentIntentId,
//               reason: 'Account upgrade error'
//             })
//           });
//         } catch (refundError) {
//           console.error('Failed to refund verification charge:', refundError);
//         }
//       }
//     } finally {
//       setBusinessPaymentProcessing(false);
//     }
//   };
//   // const handleBusinessVerificationComplete = async (paymentIntentId, paymentMethodId) => {
//   //   setBusinessPaymentIntentId(paymentIntentId);
//   //   setBusinessPaymentMethodId(paymentMethodId);
//   //   setBusinessPaymentProcessing(true);

//   //   try {
//   //     const response = await fetch('/api/users/update-account-type', {
//   //       method: 'PUT',
//   //       headers: {
//   //         'Content-Type': 'application/json',
//   //         'Authorization': `Bearer ${localStorage.getItem('token')}`
//   //       },
//   //       body: JSON.stringify({
//   //         userType: 'business',
//   //         paymentIntentId,
//   //         paymentMethodId: paymentMethodId || undefined
//   //       })
//   //     });

//   //     const data = await response.json();

//   //     if (response.ok) {
//   //       // Update local state
//   //       setFormData(prev => ({...prev, userType: 'business'}));

//   //       // Update localStorage
//   //       const userJson = localStorage.getItem('user');
//   //       if (userJson) {
//   //         const userObj = JSON.parse(userJson);
//   //         userObj.userType = 'business';
//   //         localStorage.setItem('user', JSON.stringify(userObj));
//   //       }

//   //       // Store new token if provided
//   //       if (data.token) {
//   //         localStorage.setItem('token', data.token);
//   //       }

//   //       setMessage({ type: 'success', content: 'Account upgraded to Business successfully!' });
//   //       setShowBusinessUpgradeForm(false);
//   //     } else {
//   //       setBusinessPaymentError(data.error || 'Failed to upgrade account');
//   //       // Attempt to refund the charge since upgrade failed
//   //       try {
//   //         await fetch('/api/refund-verification-payment', {
//   //           method: 'POST',
//   //           headers: {
//   //             'Content-Type': 'application/json',
//   //             'Authorization': `Bearer ${localStorage.getItem('token')}`
//   //           },
//   //           body: JSON.stringify({
//   //             paymentIntentId,
//   //             reason: 'Account upgrade failed'
//   //           })
//   //         });
//   //       } catch (refundError) {
//   //         console.error('Failed to refund verification charge:', refundError);
//   //       }
//   //     }
//   //   } catch (error) {
//   //     setBusinessPaymentError('An error occurred while upgrading account');
//   //     // Attempt to refund - keep this part as is
//   //   } finally {
//   //     setBusinessPaymentProcessing(false);
//   //   }
//   // };

//   const handleAddPaymentMethod = (updatedPaymentMethods) => {
//     setFormData(prev => ({
//       ...prev,
//       paymentMethods: updatedPaymentMethods
//     }));
//     setShowAddPaymentForm(false);
//     setMessage({ type: 'success', content: 'Payment method added successfully' });

//     // Update user object in localStorage
//     const userJson = localStorage.getItem('user');
//     if (userJson) {
//       const user = JSON.parse(userJson);
//       user.paymentMethods = updatedPaymentMethods;
//       localStorage.setItem('user', JSON.stringify(user));
//     }
//   };

//   const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
//     try {
//       const response = await fetch('/api/users/payment-methods', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         },
//         body: JSON.stringify({
//           paymentMethodId,
//           makeDefault: true
//         })
//       });

//       const data = await response.json();

//       if (response.ok) {
//         setFormData(prev => ({
//           ...prev,
//           paymentMethods: data.paymentMethods
//         }));
//         setMessage({ type: 'success', content: 'Default payment method updated' });

//         // Update user object in localStorage
//         const userJson = localStorage.getItem('user');
//         if (userJson) {
//           const user = JSON.parse(userJson);
//           user.paymentMethods = data.paymentMethods;
//           localStorage.setItem('user', JSON.stringify(user));
//         }
//       } else {
//         setMessage({ type: 'error', content: data.error || 'Failed to update payment method' });
//       }
//     } catch (error) {
//       setMessage({ type: 'error', content: 'An error occurred while updating payment method' });
//     }
//   };

//   const handleDeletePaymentMethod = async (paymentMethodId) => {
//     if (window.confirm('Are you sure you want to remove this payment method?')) {
//       try {
//         const response = await fetch(`/api/users/payment-methods/${paymentMethodId}`, {
//           method: 'DELETE',
//           headers: {
//             'Authorization': `Bearer ${localStorage.getItem('token')}`
//           }
//         });

//         const data = await response.json();

//         if (response.ok) {
//           setFormData(prev => ({
//             ...prev,
//             paymentMethods: data.paymentMethods
//           }));
//           setMessage({ type: 'success', content: 'Payment method removed' });

//           // Update user object in localStorage
//           const userJson = localStorage.getItem('user');
//           if (userJson) {
//             const user = JSON.parse(userJson);
//             user.paymentMethods = data.paymentMethods;
//             localStorage.setItem('user', JSON.stringify(user));
//           }
//         } else {
//           setMessage({ type: 'error', content: data.error || 'Failed to remove payment method' });
//         }
//       } catch (error) {
//         setMessage({ type: 'error', content: 'An error occurred while removing payment method' });
//       }
//     }
//   };

//   const handleDeleteAccount = async () => {
//     if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
//       try {
//         const response = await fetch('/api/users/profile', {
//           method: 'DELETE',
//           headers: {
//             'Authorization': `Bearer ${localStorage.getItem('token')}`
//           }
//         });

//         if (response.ok) {
//           logout();
//           navigate('/');
//         } else {
//           setMessage({ type: 'error', content: 'Failed to delete account' });
//         }
//       } catch (error) {
//         setMessage({ type: 'error', content: 'An error occurred while deleting account' });
//       }
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;

//     if (name.includes('.')) {
//       const [section, field] = name.split('.');
//       setFormData(prev => ({
//         ...prev,
//         [section]: {
//           ...prev[section],
//           [field]: type === 'checkbox' ? checked : value
//         }
//       }));
//     } else {
//       setFormData(prev => ({
//         ...prev,
//         [name]: type === 'checkbox' ? checked : value
//       }));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setMessage({ type: '', content: '' });

//     try {
//       const response = await fetch('/api/users/profile', {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         },
//         body: JSON.stringify(formData)
//       });

//       const data = await response.json();

//       if (response.ok) {

//         setMessage({ type: 'success', content: 'Profile updated successfully!' });
//         localStorage.setItem('user', JSON.stringify(data.user));

//            // Wait a short moment to show the success message
//       setTimeout(() => {
//         // Reload the page
//         window.location.reload();
//       }, 100);

//       } else {
//         setMessage({ type: 'error', content: data.error || 'Failed to update profile' });
//       }
//     } catch (error) {
//       setMessage({ type: 'error', content: 'An error occurred while updating profile' });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleLogout = () => {
//     logout();
//     navigate('/login');
//   };

//   const tabs = [
//     { id: 'personal', label: 'Account Info', icon: User },
//     { id: 'address', label: 'Address', icon: MapPin },
//     { id: 'orders', label: 'Orders', icon: Package },
//     { id: 'payments', label: 'Payment Methods', icon: CreditCard },
//     { id: 'coupons', label: 'My Coupons', icon: Tag },
//     { id: 'notifications', label: 'Notifications', icon: Bell },
//     { id: 'support', label: 'Support', icon: MessageSquare },
//     { id: 'security', label: 'Security', icon: Lock },
//     { id: 'preferences', label: 'Preferences', icon: Settings }
//   ];

//   const renderTabContent = () => {
//     switch (activeTab) {
//       case 'personal':
//         return (
//           <div className="space-y-4">
//             <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">First Name</label>
//                 <input
//                   type="text"
//                   name="firstName"
//                   value={formData.firstName}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Last Name</label>
//                 <input
//                   type="text"
//                   name="lastName"
//                   value={formData.lastName}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Email</label>
//                 <input
//                   type="email"
//                   name="email"
//                   value={formData.email}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Phone</label>
//                 <input
//                   type="tel"
//                   name="phone"
//                   value={formData.phone}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Company (Optional)</label>
//                 <input
//                   type="text"
//                   name="company"
//                   value={formData.company}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                 />
//               </div>
//               <div>
//                <label className="block text-sm font-medium mb-1">Account Type</label>
//                 <div className="space-y-2 mt-2">
//                   <div className="flex items-center justify-between p-3 border rounded-md">
//                     <div className="flex items-center">
//                       <input
//                         type="radio"
//                         id="regular-account"
//                         name="userType"
//                         checked={formData.userType === 'regular'}
//                         onChange={(e) => {
//                           e.preventDefault(); // Prevent form submission
//                           if (formData.userType !== 'regular') {
//                             if (window.confirm('Are you sure you want to change your account to Regular?')) {
//                               handleAccountTypeChange('regular');
//                             }
//                           }
//                         }}
//                         className="mr-2"
//                       />
//                       <label htmlFor="regular-account">
//                         <span className="font-medium">Regular Customer</span>
//                         <p className="text-sm text-gray-500">For personal use and shopping</p>
//                       </label>
//                     </div>
//                     {formData.userType === 'regular' && (
//                       <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Current</span>
//                     )}
//                   </div>

//                   <div className="flex items-center justify-between p-3 border rounded-md">
//                     <div className="flex items-center">
//                       <input
//                         type="radio"
//                         id="business-account"
//                         name="userType"
//                         checked={formData.userType === 'business'}
//                         onChange={(e) => {
//                           e.preventDefault(); // Prevent form submission
//                           if (formData.userType !== 'business') {
//                             if (window.confirm('Are you sure you want to upgrade to a Business account?')) {
//                               handleAccountTypeChange('business');
//                             }
//                           }
//                         }}
//                         className="mr-2"
//                       />
//                       <label htmlFor="business-account">
//                         <span className="font-medium">Business Customer</span>
//                         <p className="text-sm text-gray-500">For business purchases and bulk orders</p>
//                       </label>
//                     </div>
//                     {formData.userType === 'business' && (
//                       <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Current</span>
//                     )}
//                   </div>
//                 </div>

//                 {/* Business upgrade payment form - keep this part unchanged */}
//                 {showBusinessUpgradeForm && (
//                   <div className="mt-4 p-4 border rounded-md">
//                     <h3 className="text-lg font-medium mb-3">Business Account Verification</h3>
//                     <Elements stripe={stripePromise}>
//                       <BusinessUserPayment
//                         amount={config.businessUserChargingAmount || 1.00}
//                         email={formData.email}
//                         onPaymentComplete={handleBusinessVerificationComplete}
//                         saveCardOption={true}
//                         error={businessPaymentError}
//                         processing={businessPaymentProcessing}
//                       />
//                     </Elements>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         );

//         case 'address':
//           return (
//             <div className="space-y-6">
//               <h3 className="text-xl font-semibold">Manage Addresses</h3>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 {(formData.addresses || []).map((address, index) => (
//                   <div key={index} className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
//                     <h4 className="text-lg font-semibold text-gray-700">
//                       Address {index + 1}</h4>
//                     <div className="mt-4 space-y-2">
//                       <AddressAutocomplete
//                         index={index}
//                         address={address}
//                         onAddressChange={handleAddressChange}
//                       />

//                       <label className="block text-sm font-medium text-gray-600">City</label>
//                       <input
//                         type="text"
//                         value={address.city}
//                         onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
//                         className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                       />

//                       <label className="block text-sm font-medium text-gray-600">State/Province</label>
//                       <input
//                         type="text"
//                         value={address.state}
//                         onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
//                         className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                       />

//                       <label className="block text-sm font-medium text-gray-600">Postal Code</label>
//                       <input
//                         type="text"
//                         value={address.postalCode}
//                         onChange={(e) => handleAddressChange(index, 'postalCode', e.target.value)}
//                         className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                       />

//                       <label className="block text-sm font-medium text-gray-600">Country</label>
//                       <select
//                         value={address.country}
//                         onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
//                         className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                       >
//                         <option value="Canada">Canada</option>
//                         <option value="United States">United States</option>
//                       </select>
//                     </div>

//                     <div className="flex justify-between mt-4">
//                       <button
//                         type="button"
//                         onClick={() => handleRemoveAddress(index)}
//                         className="text-red-600 hover:text-red-800"
//                       >
//                         Remove
//                       </button>
//                       <button
//                         type="button"
//                         onClick={() => handleSetDefaultAddress(index)}
//                         className={`text-blue-600 hover:underline ${formData.defaultAddress === address._id ? 'font-bold' : ''}`}
//                       >
//                         {formData.defaultAddress === address._id ? 'Default' : 'Set as Default'}
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//               <button
//                 type="button"
//                 onClick={handleAddAddress}
//                 className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
//               >
//                 Add New Address
//               </button>
//             </div>
//           );

//         case 'orders':
//           // Ensure orders is an array
//           const orderArray = Array.isArray(orders) ? orders :
//             (orders && typeof orders === 'object' && Object.values(orders).length > 0) ?
//               Object.values(orders) : [];

//           return (
//             <div className="space-y-4">
//               <h2 className="text-xl font-semibold mb-4">Order History</h2>
//               {orderArray.length > 0 ? (
//                 <div className="grid gap-4">
//                   {orderArray.map((order) => {
//                     // Ensure order is a valid object
//                     if (!order || typeof order !== 'object') return null;

//                     const orderId = order._id || 'unknown';
//                     const createdAt = order.createdAt ? new Date(order.createdAt) : new Date();
//                     const totalAmount = order.totalAmount || 0;
//                     const status = order.status || 'pending';

//                     return (
//                       <div
//                         key={orderId}
//                         className="bg-white rounded-lg shadow p-4 border border-gray-200"
//                       >
//                         <div className="flex justify-between items-center mb-2">
//                           <h3 className="font-medium text-gray-800 truncate">
//                             Order #{typeof orderId === 'string' ? orderId.substring(Math.max(0, orderId.length - 8)) : orderId}
//                           </h3>
//                           <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full
//                             ${status === 'completed' ? 'bg-green-100 text-green-800' :
//                               status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
//                               status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                               'bg-blue-100 text-blue-800'}`}>
//                             {status.charAt(0).toUpperCase() + status.slice(1)}
//                           </span>
//                         </div>

//                         <div className="grid grid-cols-2 gap-2 text-sm">
//                           <div>
//                             <p className="text-gray-500">Date</p>
//                             <p>{createdAt.toLocaleDateString()}</p>
//                           </div>
//                           <div>
//                             <p className="text-gray-500">Total</p>
//                             <p className="font-medium">${parseFloat(totalAmount).toFixed(2)}</p>
//                           </div>
//                         </div>

//                         <div className="mt-4 pt-2 border-t border-gray-100">
//                           <a
//                             href={`/orders`}
//                             className="block w-full text-center py-2 text-blue-600 font-medium rounded hover:bg-blue-50"
//                           >
//                             View Details
//                           </a>
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               ) : (
//                 <div className="bg-white rounded-lg shadow p-6 text-center">
//                 <div className="flex justify-center mb-4">
//                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 160" className="w-40">
//                     {/* Background with subtle gradient */}
//                     <defs>
//                       <linearGradient id="emptyOrderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
//                         <stop offset="0%" stopColor="#fcfcfd" stopOpacity="1" />
//                         <stop offset="100%" stopColor="#f9fafb" stopOpacity="1" />
//                       </linearGradient>
//                       <filter id="orderShadow" x="-10%" y="-10%" width="120%" height="120%">
//                         <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.05"/>
//                       </filter>
//                     </defs>

//                     {/* Order paper */}
//                     <rect x="70" y="20" width="100" height="120" rx="4" fill="white" stroke="#e5e7eb" strokeWidth="1.5" filter="url(#orderShadow)" />

//                     {/* Order lines */}
//                     <line x1="85" y1="40" x2="155" y2="40" stroke="#e5e7eb" strokeWidth="1.5" />
//                     <line x1="85" y1="60" x2="155" y2="60" stroke="#e5e7eb" strokeWidth="1.5" />
//                     <line x1="85" y1="80" x2="155" y2="80" stroke="#e5e7eb" strokeWidth="1.5" />
//                     <line x1="85" y1="100" x2="155" y2="100" stroke="#e5e7eb" strokeWidth="1.5" />

//                     {/* Search icon */}
//                     <circle cx="150" cy="70" r="30" fill="none" stroke="#9ca3af" strokeWidth="1.5" opacity="0.7" />
//                     <line x1="170" y1="90" x2="185" y2="105" stroke="#9ca3af" strokeWidth="2" opacity="0.7" />

//                     {/* X mark for "not found" */}
//                     <line x1="120" y1="50" x2="140" y2="70" stroke="#d1d5db" strokeWidth="2" />
//                     <line x1="140" y1="50" x2="120" y2="70" stroke="#d1d5db" strokeWidth="2" />
//                   </svg>
//                 </div>
//                 <p className="text-gray-500">No orders found.</p>
//               </div>
//               )}
//             </div>
//           );

//         case 'coupons':
//           return (
//             <div className="space-y-4">
//               <h2 className="text-xl font-semibold mb-4">My Coupons</h2>
//               {coupons.length > 0 ? (
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {coupons.map((coupon) => (
//                     <div key={coupon._id} className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
//                       <div className="flex justify-between items-start">
//                         <div className="space-y-2">
//                           <div className="flex items-center space-x-2">
//                             <Tag className="h-5 w-5 text-blue-600" />
//                             <h3 className="text-lg font-medium text-blue-600">{coupon.code}</h3>
//                           </div>
//                           <p className="text-2xl font-bold">
//                             {coupon.discountType === 'percentage'
//                               ? `${coupon.discountValue}% OFF`
//                               : `$${coupon.discountValue} OFF`}
//                           </p>
//                           <div className="space-y-1">
//                             <p className="text-sm text-gray-600">
//                               Minimum purchase: ${coupon.minimumPurchase || 0}
//                             </p>
//                             {coupon.maxUsesPerUser > 0 && (
//                               <p className="text-sm text-gray-600">
//                                 Max Uses: {coupon.maxUsesPerUser - (coupon.userUsage?.usageCount || 0)}
//                               </p>
//                             )}
//                           </div>
//                         </div>
//                         <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
//                           new Date(coupon.endDate) > new Date()
//                             ? 'bg-green-100 text-green-800'
//                             : 'bg-red-100 text-red-800'
//                         }`}>
//                           {new Date(coupon.endDate) > new Date() ? 'Active' : 'Expired'}
//                         </span>
//                       </div>
//                       <div className="mt-4 pt-4 border-t">
//                         <div className="flex justify-between items-center">
//                           <div className="text-sm text-gray-500">
//                             Valid until: {new Date(coupon.endDate).toLocaleDateString()}
//                           </div>
//                           {/* <button
//                             onClick={() => navigator.clipboard.writeText(coupon.code)}
//                             className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
//                           >
//                             Copy Code
//                           </button> */}
//                           <CopyButton code={coupon.code} />

//                         </div>
//                         {coupon.conditions && (
//                           <p className="mt-2 text-sm text-gray-500">
//                             Conditions: {coupon.conditions}
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               ) : (
//                 <div className="text-center py-8 bg-gray-50 rounded-lg">
//                   <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
//                   <h3 className="text-lg font-medium text-gray-900">No Coupons Available</h3>
//                   <p className="mt-2 text-gray-500">Check back later for new offers and discounts.</p>
//                 </div>
//               )}
//             </div>
//           );

//       case 'support':
//         return <div><SupportSection /></div>;

//       case 'payments':
//         return (
//           <div className="space-y-4">
//             <div className="flex justify-between items-center">
//               <h2 className="text-xl font-semibold">Payment Methods</h2>
//               <button
//                 onClick={() => setShowAddPaymentForm(true)}
//                 className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
//               >
//                 <Plus className="h-4 w-4 mr-2" />
//                 Add New Card
//               </button>
//             </div>

//             {formData.paymentMethods && formData.paymentMethods.length > 0 ? (
//               <div className="space-y-4">
//                 {formData.paymentMethods.map((method, index) => (
//                   <div key={index} className="border rounded-lg p-4 bg-white flex justify-between items-center">
//                     <div className="flex items-center space-x-4">
//                       <div className="w-10 flex-shrink-0">
//                         {method.brand === 'visa' && <span className="text-blue-600 text-xl">Visa</span>}
//                         {method.brand === 'mastercard' && <span className="text-red-600 text-xl">MC</span>}
//                         {method.brand === 'amex' && <span className="text-blue-700 text-xl">Amex</span>}
//                         {!['visa', 'mastercard', 'amex'].includes(method.brand) && <CreditCard className="h-6 w-6 text-gray-500" />}
//                       </div>
//                       <div>
//                         <p className="font-medium">•••• •••• •••• {method.last4}</p>
//                         <p className="text-sm text-gray-500">Expires {method.expiryMonth}/{method.expiryYear}</p>
//                         {method.isDefault && (
//                           <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">Default</span>
//                         )}
//                       </div>
//                     </div>
//                     <div className="flex space-x-2">
//                       {!method.isDefault && (
//                         <button
//                           onClick={() => handleSetDefaultPaymentMethod(method.paymentMethodId)}
//                           className="text-blue-600 hover:text-blue-800"
//                         >
//                           Set Default
//                         </button>
//                       )}
//                       <button
//                         onClick={() => handleDeletePaymentMethod(method.paymentMethodId)}
//                         className="text-red-600 hover:text-red-800"
//                       >
//                         <Trash2 className="h-5 w-5" />
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             ) : (
//               <div className="text-center py-8 bg-gray-50 rounded-lg">
//                 <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
//                 <h3 className="text-lg font-medium text-gray-900">No Payment Methods Added</h3>
//                 <p className="mt-2 text-gray-500">Add a payment method to speed up checkout.</p>
//               </div>
//             )}

//             {showAddPaymentForm && (
//               <div className="mt-4 p-4 border rounded-md">
//                 <h3 className="text-lg font-medium mb-3">Add Payment Method</h3>
//                 <Elements stripe={stripePromise}>
//                   <PaymentMethodForm
//                     onComplete={handleAddPaymentMethod}
//                     onCancel={() => setShowAddPaymentForm(false)}
//                     error={paymentMethodError}
//                   />
//                 </Elements>
//               </div>
//             )}
//           </div>
//         );
//         // return (
//         //   <div className="space-y-4">
//         //     <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
//         //     <div className="space-y-4">
//         //       {/* Sample saved payment methods */}
//         //       <div className="border rounded-lg p-4 bg-white">
//         //         <div className="flex justify-between items-center">
//         //           <div className="flex items-center space-x-4">
//         //             <CreditCard className="h-6 w-6 text-gray-500" />
//         //             <div>
//         //               <p className="font-medium">•••• •••• •••• 4242</p>
//         //               <p className="text-sm text-gray-500">Expires 12/25</p>
//         //             </div>
//         //           </div>
//         //           <button className="text-red-600 hover:text-red-800">
//         //             <Trash2 className="h-5 w-5" />
//         //           </button>
//         //         </div>
//         //       </div>

//         //       <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
//         //         <Plus className="h-5 w-5" />
//         //         <span>Add New Payment Method</span>
//         //       </button>
//         //     </div>
//         //   </div>
//         // );

//         case 'security':
//           return (
//             <div className="space-y-4">
//               <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
//               <div className="space-y-6">
//                 {/* Password Change Section */}
//                 <div className="bg-white p-6 rounded-lg">
//                   <h3 className="text-lg font-medium mb-4">Change Password</h3>
//                   <div className="space-y-4">
//                     <div className="relative">
//                       <label className="block text-sm font-medium mb-1">Current Password</label>
//                       <div className="relative">
//                         <input
//                           type={showCurrentPassword ? "text" : "password"}
//                           name="currentPassword"
//                           value={formData.currentPassword}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                         />
//                         <button
//                           type="button"
//                           onClick={() => setShowCurrentPassword(!showCurrentPassword)}
//                           className="absolute right-2 top-1/2 -translate-y-1/2"
//                           style={{
//                             boxShadow: 'none',
//                             border: 'none',
//                             background: 'transparent',
//                             transform: 'none',
//                             padding: 0,
//                             overflow: 'visible',
//                             marginTop: -10+'px'
//                           }}
//                         >
//                           {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
//                         </button>
//                       </div>
//                     </div>

//                     <div className="relative">
//                       <label className="block text-sm font-medium mb-1">New Password</label>
//                       <div className="relative">
//                         <input
//                           type={showNewPassword ? "text" : "password"}
//                           name="newPassword"
//                           value={formData.newPassword}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
//                         />
//                         <button
//                           type="button"
//                           onClick={() => setShowNewPassword(!showNewPassword)}
//                           className="absolute right-2 top-1/2 -translate-y-1/2"
//                           style={{
//                             boxShadow: 'none',
//                             border: 'none',
//                             background: 'transparent',
//                             transform: 'none',
//                             padding: 0,
//                             overflow: 'visible',
//                             marginTop: -10+'px'
//                           }}
//                         >
//                           {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Login Activity Section */}
//                 <div className="bg-white p-6 rounded-lg">
//                   <h3 className="text-lg font-medium mb-4">Login Activity</h3>
//                   <div className="space-y-4">
//                     {loadingActivity ? (
//                       <div className="flex justify-center">
//                         <Loader2 className="animate-spin" size={24} />
//                       </div>
//                     ) : (
//                       <>
//                         {loginActivity.slice(0, showAllActivity ? undefined : 1).map((activity, index) => (
//                           <div key={index} className="flex justify-between items-center border-b pb-2">
//                             <div>
//                               <p className="font-medium">Login from {activity.location || 'Unknown Location'}</p>
//                               <p className="text-sm text-gray-500">
//                                 {activity.device ? activity.device.split('(')[0] : 'Unknown Device'}
//                               </p>
//                             </div>
//                             <p className="text-sm text-gray-500">
//                               {new Date(activity.timestamp).toLocaleString()}
//                             </p>
//                           </div>
//                         ))}
//                         <button
//                           type="button"
//                           onClick={(e) => {
//                             e.preventDefault();
//                             setShowAllActivity(!showAllActivity);
//                           }}
//                           className="text-blue-600 hover:text-blue-800 text-sm"
//                         >
//                           {showAllActivity ? 'Show Less' : 'View all activity'}
//                         </button>
//                       </>
//                     )}
//                   </div>
//                 </div>

//                 {/* Account Deletion Section */}
//                 {/* <div className="bg-white p-6 rounded-lg">
//                   <h3 className="text-lg font-medium mb-4">Account Deletion</h3>
//                   <p className="text-sm text-gray-500 mb-4">
//                     Once you delete your account, there is no going back. Please be certain.
//                   </p>
//                   <button
//                     type="button"
//                     onClick={() => handleDeleteAccount()}
//                     className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
//                   >
//                     Delete Account
//                   </button>
//                 </div> */}
//               </div>
//             </div>
//           );

//         // And here's the notification case:
//         case 'notifications':
//           const handlePreferenceChange = async (e) => {
//             e.preventDefault();
//             const { name, checked } = e.target;
//             const [section, field] = name.split('.');

//             try {
//               const response = await fetch('/api/users/preferences', {
//                 method: 'PUT',
//                 headers: {
//                   'Content-Type': 'application/json',
//                   'Authorization': `Bearer ${localStorage.getItem('token')}`
//                 },
//                 body: JSON.stringify({
//                   preferences: {
//                     ...formData.preferences,
//                     [field]: checked
//                   }
//                 })
//               });

//               if (response.ok) {
//                 setFormData(prev => ({
//                   ...prev,
//                   [section]: {
//                     ...prev[section],
//                     [field]: checked
//                   }
//                 }));
//                 setMessage({ type: 'success', content: 'Preferences updated successfully!' });
//               } else {
//                 setMessage({ type: 'error', content: 'Failed to update preferences' });
//               }
//             } catch (error) {
//               console.error('Error updating preferences:', error);
//               setMessage({ type: 'error', content: 'Error updating preferences' });
//             }
//           };

//           return (
//             <div className="space-y-4">
//               <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
//               <div className="space-y-4">
//                 <div className="flex items-center justify-between p-4 bg-white rounded-lg">
//                   <div>
//                     <h3 className="font-medium">Order Updates</h3>
//                     <p className="text-sm text-gray-500">Receive notifications about your order status</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       name="preferences.orderUpdates"
//                       checked={formData.preferences.orderUpdates}
//                       onChange={handlePreferenceChange}
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
//                   </label>
//                 </div>

//                 <div className="flex items-center justify-between p-4 bg-white rounded-lg">
//                   <div>
//                     <h3 className="font-medium">Promotional Emails</h3>
//                     <p className="text-sm text-gray-500">Receive emails about sales and special offers</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       name="preferences.promotionalAlerts"
//                       checked={formData.preferences.promotionalAlerts}
//                       onChange={handlePreferenceChange}
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
//                   </label>
//                 </div>

//                 <div className="flex items-center justify-between p-4 bg-white rounded-lg">
//                   <div>
//                     <h3 className="font-medium">Newsletter</h3>
//                     <p className="text-sm text-gray-500">Receive our monthly newsletter</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       name="preferences.newsletter"
//                       checked={formData.preferences.newsletter}
//                       onChange={handlePreferenceChange}
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
//                   </label>
//                 </div>
//               </div>
//             </div>
//           );

//       case 'preferences':
//         return (
//           <div className="space-y-4">
//             <h2 className="text-xl font-semibold mb-4">Account Preferences</h2>
//             <div className="space-y-4">
//               <div className="bg-white p-6 rounded-lg">
//                 <h3 className="text-lg font-medium mb-4">Language & Region</h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium mb-1">Language</label>
//                     <select
//                       className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
//                       defaultValue="en"
//                     >
//                       <option value="en">English</option>
//                       <option value="fr">Français</option>
//                     </select>
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium mb-1">Time Zone</label>
//                     <select
//                       className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
//                       defaultValue="EST"
//                     >
//                       <option value="EST">Eastern Time (ET)</option>
//                       <option value="CST">Central Time (CT)</option>
//                       <option value="MST">Mountain Time (MT)</option>
//                       <option value="PST">Pacific Time (PT)</option>
//                     </select>
//                   </div>
//                 </div>
//               </div>

//               <div className="bg-white p-6 rounded-lg">
//                 <h3 className="text-lg font-medium mb-4">Privacy Settings</h3>
//                 <div className="space-y-4">
//                   <div className="flex items-center justify-between">
//                     <div>
//                       <p className="font-medium">Profile Visibility</p>
//                       <p className="text-sm text-gray-500">Control who can see your profile</p>
//                     </div>
//                     <select
//                       className="p-2 border rounded focus:ring-2 focus:ring-blue-500"
//                       defaultValue="private"
//                     >
//                       <option value="public">Public</option>
//                       <option value="private">Private</option>
//                     </select>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         );

//       default:
//         return null;
//     }
//   };

//   return (
//     <div className="py-16">
//       <div className="max-w-7xl mx-auto px-4 py-8">
//         <div className="flex flex-col md:flex-row gap-8">
//           {/* Sidebar */}
//           <div className="w-full md:w-64 space-y-1">
//             {tabs.map((tab) => {
//               const Icon = tab.icon;
//               return (
//                 <button
//                   key={tab.id}
//                   onClick={() => {
//                     setActiveTab(tab.id);
//                     if (window.innerWidth <= 768) {
//                       window.scrollBy({ top: 300, behavior: 'smooth' });
//                     }
//                   }}
//                   className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
//                     activeTab === tab.id
//                       ? 'bg-blue-200 font-normal text-blue-900'
//                       : 'text-gray-600 font-normal hover:bg-gray-200'
//                   }`}
//                 >
//                   <Icon className="h-5 w-5" />
//                   <span>{tab.label}</span>
//                 </button>
//               );
//             })}

//             <button
//               onClick={handleLogout}
//               className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
//             >
//               <LogOut className="h-5 w-5" />
//               <span>Log Out</span>
//             </button>
//           </div>

//           {/* Main Content
//           <div className="flex-1">
//             <div className="bg-white rounded-lg p-6 shadow-sm">
//               {message.content && (
//                 <Alert variant={message.type === 'error' ? 'destructive' : 'default'} className="mb-6">
//                   <AlertDescription>{message.content}</AlertDescription>
//                 </Alert>
//               )}

//               <form onSubmit={handleSubmit}>
//                 {renderTabContent()}

//                 {['personal', 'address', 'notifications', 'security', 'preferences'].includes(activeTab) && (
//                   <div className="mt-6 flex justify-end">
//                     <button
//                       type="submit"
//                       disabled={loading}
//                       className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
//                     >
//                       {loading ? (
//                         <>
//                           <Loader2 className="animate-spin mr-2" size={20} />
//                           Saving...
//                         </>
//                       ) : (
//                         <>
//                           <Save className="mr-2" size={20} />
//                           Save Changes
//                         </>
//                       )}
//                     </button>
//                   </div>
//                 )}
//               </form>
//             </div>
//           </div> */}
//           {/* Main Content */}
// <div className="flex-1">
//   <div className="bg-white rounded-lg p-6 shadow-sm">
//     {message.content && (
//       <Alert variant={message.type === 'error' ? 'destructive' : 'default'} className="mb-6">
//         <AlertDescription>{message.content}</AlertDescription>
//       </Alert>
//     )}

//     {/* Only wrap form around editable sections */}
//     {['personal', 'address', 'notifications', 'security', 'preferences'].includes(activeTab) ? (
//       <form onSubmit={handleSubmit}>
//         {renderTabContent()}
//         <div className="mt-6 flex justify-end">
//           <button
//             type="submit"
//             disabled={loading}
//             className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
//           >
//             {loading ? (
//               <>
//                 <Loader2 className="animate-spin mr-2" size={20} />
//                 Saving...
//               </>
//             ) : (
//               <>
//                 <Save className="mr-2" size={20} />
//                 Save Changes
//               </>
//             )}
//           </button>
//         </div>
//       </form>
//     ) : (
//       renderTabContent()
//     )}
//   </div>
// </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Profile;
